import { sub } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import { DatePickerWithRange } from '@/components/ui/DateRangePicker'
import { RadioGroup, RadioGroupButtonItem } from '@/components/ui/RadioGroup'
import {
  convertValueToLabel,
  DateType,
  getAllRangesForDate,
} from '@/utils/date'

// Constants
const DATE_PICKER_CONSTANTS = {
  CUSTOM: 'custom' as const,
  TEST_RANGE: 'test-range' as const,
  MAX_HEIGHT: 'max-h-[300px]',
  MAX_WIDTH: 'min-w-[160px]',
  BUTTON_CLASSES: 'items-center justify-center inline-flex',
  TEST_RANGE_DATES: {
    from: new Date('2024-07-01'),
    to: new Date('2024-07-31'),
  },
} as const

interface ScalrDatePickerProps {
  date: DateRange | undefined
  dateFilterType: DateType
  onChange?: (
    newDate: DateRange | undefined,
    newDateFilterType: DateType
  ) => void
  // Legacy props for backward compatibility
  setDate?: React.Dispatch<React.SetStateAction<DateRange | undefined>>
  setDateFilterType?: React.Dispatch<React.SetStateAction<DateType>>
  // Configuration options
  showTestRange?: boolean
  showChildren?: boolean
  fixedWidth?: string
}

// Helper component for radio group items
const DateRangeOption = ({
  value,
  title,
  description,
  className = DATE_PICKER_CONSTANTS.BUTTON_CLASSES,
}: {
  value: string
  title: string
  description: string
  className?: string
}) => (
  <RadioGroupButtonItem className={className} value={value} id={value}>
    <div className="flex flex-col text-muted-foreground">
      {title}
      <span className="text-xs text-muted-foreground">{description}</span>
    </div>
  </RadioGroupButtonItem>
)

const ScalrDatePicker = ({
  date,
  dateFilterType,
  onChange,
  setDate,
  setDateFilterType,
  showTestRange = true,
  showChildren = true,
  fixedWidth,
}: ScalrDatePickerProps) => {
  const { t } = useTranslation()

  // Use controlled state when using new interface, otherwise use internal state
  const useControlledState = onChange !== undefined

  const [internalDate, setInternalDate] = useState<DateRange | undefined>(date)
  const [internalDateFilterType, setInternalDateFilterType] =
    useState<DateType>(dateFilterType)

  // Current values - either from props (controlled) or internal state (uncontrolled)
  const currentDate = useControlledState ? date : internalDate
  const currentDateFilterType = useControlledState
    ? dateFilterType
    : internalDateFilterType

  // Sync internal state with props when using legacy interface
  useEffect(() => {
    if (!useControlledState) {
      setInternalDate(date)
      setInternalDateFilterType(dateFilterType)
    }
  }, [date, dateFilterType, useControlledState])

  // Memoize date ranges to avoid recalculation
  const dateRanges = useMemo(() => getAllRangesForDate(), [])

  // Memoize formatted date ranges for display
  const formattedDateRanges = useMemo(
    () =>
      dateRanges.map((range) => ({
        ...range,
        displayText: `${range.from.toLocaleDateString()}${
          range.to ? ` - ${range.to.toLocaleDateString()}` : ''
        }`,
      })),
    [dateRanges]
  )

  // Get default date for custom range
  const getDefaultCustomDate = useCallback(
    () => ({
      from: currentDate?.from ?? sub(new Date(), { days: 1 }),
      to: currentDate?.to ?? undefined,
    }),
    [currentDate]
  )

  // Handle date range selection
  const handleRangeSelect = useCallback(
    (range: { from: Date; to: Date | undefined }, value: DateType) => {
      const newDateRange = { from: range.from, to: range.to }

      if (useControlledState && onChange) {
        // New interface - call onChange callback
        onChange(newDateRange, value)
      } else {
        // Legacy interface - update internal state and call setters
        setInternalDate(newDateRange)
        setInternalDateFilterType(value)

        if (setDate) setDate(newDateRange)
        if (setDateFilterType) setDateFilterType(value)
      }
    },
    [useControlledState, onChange, setDate, setDateFilterType]
  )

  // Handle radio group value changes
  const handleRadioGroupChange = useCallback(
    (value: string) => {
      if (value === DATE_PICKER_CONSTANTS.CUSTOM) {
        handleRangeSelect(getDefaultCustomDate(), DATE_PICKER_CONSTANTS.CUSTOM)
      } else if (value === DATE_PICKER_CONSTANTS.TEST_RANGE) {
        handleRangeSelect(
          DATE_PICKER_CONSTANTS.TEST_RANGE_DATES,
          DATE_PICKER_CONSTANTS.TEST_RANGE
        )
      } else {
        const selectedRange = dateRanges.find((range) => range.type === value)
        if (selectedRange) {
          handleRangeSelect(
            {
              from: selectedRange.from,
              to: selectedRange.to,
            },
            selectedRange.type as DateType
          )
        }
      }
    },
    [dateRanges, handleRangeSelect, getDefaultCustomDate]
  )

  // Handle date picker changes (for custom date selection)
  const handleDatePickerChange = useCallback(
    (
      newDate:
        | DateRange
        | undefined
        | ((prevState: DateRange | undefined) => DateRange | undefined)
    ) => {
      const resolvedDate =
        typeof newDate === 'function' ? newDate(currentDate) : newDate
      if (resolvedDate?.from) {
        handleRangeSelect(
          {
            from: resolvedDate.from,
            to: resolvedDate.to,
          },
          DATE_PICKER_CONSTANTS.CUSTOM
        )
      }
    },
    [handleRangeSelect, currentDate]
  )

  return (
    <div>
      <DatePickerWithRange
        date={currentDate}
        setDate={handleDatePickerChange}
        buttonWidth={fixedWidth}
        children={
          showChildren && (
            <div
              className={`flex flex-col items-center overflow-y-auto ${DATE_PICKER_CONSTANTS.MAX_HEIGHT} ${DATE_PICKER_CONSTANTS.MAX_WIDTH} pr-2`}
            >
              <RadioGroup
                value={currentDateFilterType}
                onValueChange={handleRadioGroupChange}
              >
                <div className="flex flex-col space-y-2">
                  <DateRangeOption
                    value={DATE_PICKER_CONSTANTS.CUSTOM}
                    title={t('datePicker.customRange')}
                    description={t('datePicker.selectAnyDate')}
                  />

                  {showTestRange && (
                    <DateRangeOption
                      value={DATE_PICKER_CONSTANTS.TEST_RANGE}
                      title={t('datePicker.testRange')}
                      description="7/1/2024 - 7/31/2024"
                    />
                  )}

                  {formattedDateRanges.map((range) => (
                    <DateRangeOption
                      key={range.type}
                      value={range.type}
                      title={convertValueToLabel(range.type)}
                      description={range.displayText}
                    />
                  ))}
                </div>
              </RadioGroup>
            </div>
          )
        }
      />
    </div>
  )
}

export { ScalrDatePicker }
